import { DefaultOptionType } from 'antd/lib/select';
import { useMemo } from 'react';
import { useProgramTypeService } from 'shared/services/programTypeService';

export const formatProgramType = (label?: string | null, percentage?: number | null) =>
  label && percentage !== null && percentage !== undefined ? `${label} — ${percentage}%` : '—';

interface CurrentProgramType {
  value?: string | null;
  label?: string | null;
}

/**
 * Program Type dropdown options from the API, in display order.
 * When the saved type has been deactivated it is no longer returned, so it is added back as a
 * disabled "(inactive)" option to keep the form showing what is saved.
 */
export const useProgramTypeOptions = (current?: CurrentProgramType) => {
  const { getProgramTypeOptions } = useProgramTypeService();
  const { data, isLoading } = getProgramTypeOptions();

  const types = useMemo(
    () =>
      (data?.success && Array.isArray(data.data) ? data.data : [])
        .filter((type) => type.is_active)
        .map((type) => ({ value: type.key, label: type.label, percentage: type.percentage })),
    [data],
  );

  const options: DefaultOptionType[] = useMemo(() => {
    const list: DefaultOptionType[] = types.map((type) => ({
      value: type.value,
      label: formatProgramType(type.label, type.percentage),
    }));
    if (current?.value && !types.some((type) => type.value === current.value)) {
      list.push({ value: current.value, label: `${current.label || current.value} (inactive)`, disabled: true });
    }
    return list;
  }, [types, current?.value, current?.label]);

  const getPercentage = (value?: string | null) => types.find((type) => type.value === value)?.percentage ?? null;

  return { options, types, loading: isLoading, getPercentage };
};
